import { type ScheduledOutreachState } from '@apps/web/lib/outreach_utils';
import { type DisplayOutreachStatus } from '@libraries/typescript/api_client';

export function getFoundAvailableCaregiversLabel(
    acceptedCaregiverCount: number,
): string {
    const caregiverLabel =
        acceptedCaregiverCount === 1 ? 'caregiver' : 'caregivers';
    return `Found ${acceptedCaregiverCount} available ${caregiverLabel}`;
}

export function showsFoundAvailableCaregiversStatus({
    status,
    acceptedCaregiverCount,
}: {
    readonly status: DisplayOutreachStatus;
    readonly acceptedCaregiverCount?: number;
}): boolean {
    return status === 'unfilled' && (acceptedCaregiverCount ?? 0) > 0;
}

export function getOutreachBadgeDisplayStatus({
    status,
    acceptedCaregiverCount,
    openAcceptedCaregiverCount,
}: {
    readonly status: DisplayOutreachStatus;
    readonly acceptedCaregiverCount?: number;
    // Caregivers with a yes on a still-open shift. accepted_count keeps
    // counting caregivers whose accepted shifts were since assigned, so on
    // a multi-shift outreach only the open count says whether a yes is
    // still actionable. null/undefined (endpoint did not compute it) falls
    // back to acceptedCaregiverCount.
    readonly openAcceptedCaregiverCount?: number | null;
}): DisplayOutreachStatus {
    const actionableCount =
        openAcceptedCaregiverCount ?? acceptedCaregiverCount ?? 0;
    if (
        status === 'awaiting_confirmation' &&
        openAcceptedCaregiverCount !== null &&
        openAcceptedCaregiverCount !== undefined &&
        actionableCount <= 0
    ) {
        return 'in_progress';
    }
    if (status === 'in_progress' && actionableCount > 0) {
        return 'awaiting_confirmation';
    }

    return status;
}

function showsCancelledWithAvailableCaregiversLabel({
    status,
    acceptedCaregiverCount,
    hasAssignedCaregiver,
}: {
    readonly status: DisplayOutreachStatus;
    readonly acceptedCaregiverCount?: number;
    readonly hasAssignedCaregiver?: boolean;
}): boolean {
    return (
        status === 'cancelled' &&
        (acceptedCaregiverCount ?? 0) > 0 &&
        hasAssignedCaregiver === false
    );
}

export function getOutreachStatusLabel({
    status,
    acceptedCaregiverCount,
    openAcceptedCaregiverCount,
    hasAssignedCaregiver,
    scheduledState,
}: {
    readonly status: DisplayOutreachStatus;
    readonly acceptedCaregiverCount?: number;
    // See getOutreachBadgeDisplayStatus: only the awaiting-confirmation
    // label uses this — terminal labels (unfilled/archived) keep the
    // historical accepted count.
    readonly openAcceptedCaregiverCount?: number | null;
    readonly hasAssignedCaregiver?: boolean;
    readonly scheduledState?: ScheduledOutreachState;
}): string {
    if (
        showsFoundAvailableCaregiversStatus({ status, acceptedCaregiverCount })
    ) {
        return getFoundAvailableCaregiversLabel(acceptedCaregiverCount ?? 0);
    }

    if (
        showsCancelledWithAvailableCaregiversLabel({
            status,
            acceptedCaregiverCount,
            hasAssignedCaregiver,
        })
    ) {
        return `${acceptedCaregiverCount ?? 0} Available - Archived`;
    }

    switch (status) {
        case 'pending':
            if (scheduledState === 'paused') {
                return 'Scheduled (paused)';
            }
            if (scheduledState === 'scheduled') {
                return 'Scheduled';
            }
            return 'Drafted';
        case 'paused':
            return 'Paused';
        case 'in_progress':
            return 'In Progress';
        case 'awaiting_confirmation': {
            const actionableCount =
                openAcceptedCaregiverCount ?? acceptedCaregiverCount;
            if (actionableCount === undefined || actionableCount <= 0) {
                return 'Pending Confirmation';
            }
            return `${actionableCount} caregiver${
                actionableCount === 1 ? '' : 's'
            } available`;
        }
        case 'filled':
            return 'Filled';
        case 'unfilled':
            return 'Unfilled';
        case 'cancelled':
            return 'Archived';
        case 'shift_missing':
            return 'Shift Missing';
        default:
            return status;
    }
}
